// frontend/src/components/CampaignManager/tabs/LotteriesTab.tsx
import React, { useState } from 'react';
import { formatDate } from '../../../utils/dateUtils';
import {
  Box,
  Button,
  Stack,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  IconButton,
  Chip,
  useMediaQuery,
  useTheme,
  Collapse,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Campaign as CampaignIcon
} from '@mui/icons-material';
import { LotteryCard } from '../MobileCards';
import { Lottery, Campaign } from '../types';

interface Props {
  lotteries: Lottery[];
  campaigns?: Campaign[];
  deleteLottery: (id: number) => Promise<void>;
  onOpenDialog: (lottery: Lottery | null) => void;
  onNavigateToCampaigns?: (lotteryId: number) => void;
}

export default function LotteriesTab({ lotteries, campaigns = [], deleteLottery, onOpenDialog, onNavigateToCampaigns }: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expandedLotteryId, setExpandedLotteryId] = useState<number | null>(null);

  const handleDelete = async (id: number) => {
    if (window.confirm('Lotterie wirklich löschen?')) {
      try {
        await deleteLottery(id);
      } catch (error) {
        console.error('Error deleting lottery:', error);
      }
    }
  };

  const getCampaignsForLottery = (lotteryId: number) => {
    return campaigns.filter(c => c.lottery_id === lotteryId);
  };

  const toggleExpand = (lotteryId: number) => {
    setExpandedLotteryId(expandedLotteryId === lotteryId ? null : lotteryId);
  };

  return (
    <Box>
      <Stack 
        direction="row" 
        justifyContent="space-between" 
        alignItems="center" 
        sx={{ mb: 2 }}
      >
        <Typography variant="h6">Lotterien</Typography>
        <Button
          variant="contained"
          size={isMobile ? 'small' : 'medium'}
          startIcon={<AddIcon />}
          onClick={() => onOpenDialog(null)}
          sx={{ backgroundColor: '#003366' }}
        >
          {isSmallMobile ? 'Neu' : 'Neue Lotterie'}
        </Button>
      </Stack>

      {isMobile ? (
        // Mobile: Card Layout
        lotteries.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Keine Lotterien vorhanden
            </Typography>
          </Paper>
        ) : (
          lotteries.map((lottery) => (
            <LotteryCard
              key={lottery.id}
              lottery={lottery}
              onEdit={onOpenDialog}
              onDelete={handleDelete}
            />
          ))
        )
      ) : (
        // Desktop: Table Layout
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={40}></TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Kurzname</TableCell>
                <TableCell>Jahr</TableCell>
                <TableCell>Typ</TableCell>
                <TableCell>Kampagnen</TableCell>
                <TableCell>Startdatum</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lotteries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      Keine Lotterien vorhanden
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                lotteries.map((lottery) => {
                  const lotteryCampaigns = getCampaignsForLottery(lottery.id);
                  const isExpanded = expandedLotteryId === lottery.id;
                  return (
                    <React.Fragment key={lottery.id}>
                      <TableRow hover>
                        <TableCell>
                          {lotteryCampaigns.length > 0 && (
                            <IconButton
                              size="small"
                              onClick={() => toggleExpand(lottery.id)}
                            >
                              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell>
                          <strong>{lottery.name}</strong>
                        </TableCell>
                        <TableCell>{lottery.name_kurz}</TableCell>
                        <TableCell>{lottery.year}</TableCell>
                        <TableCell>
                          <Chip
                            label={lottery.type}
                            color={lottery.type === 'SKL' ? 'primary' : 'secondary'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={lotteryCampaigns.length > 0 ? "Klicken zum Anzeigen" : "Keine Kampagnen"}>
                            <Chip
                              icon={<CampaignIcon />}
                              label={lotteryCampaigns.length}
                              size="small"
                              color={lotteryCampaigns.length > 0 ? 'info' : 'default'}
                              onClick={lotteryCampaigns.length > 0 ? () => toggleExpand(lottery.id) : undefined}
                              sx={{ cursor: lotteryCampaigns.length > 0 ? 'pointer' : 'default' }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {formatDate(lottery.startdatum)}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => onOpenDialog(lottery)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(lottery.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      {lotteryCampaigns.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 2, px: 2, bgcolor: 'grey.50', borderRadius: 1, my: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Kampagnen für {lottery.name}
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Name</TableCell>
                                      <TableCell>Kurz</TableCell>
                                      <TableCell>Zeitraum</TableCell>
                                      <TableCell>Status</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {lotteryCampaigns.map((campaign) => (
                                      <TableRow key={campaign.id}>
                                        <TableCell>{campaign.name}</TableCell>
                                        <TableCell>{campaign.kurz || '-'}</TableCell>
                                        <TableCell>
                                          {formatDate(campaign.start_date)}
                                        </TableCell>
                                        <TableCell>
                                          <Chip label={campaign.status} size="small" />
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
